import React, { useState, useMemo, useEffect, useCallback } from 'react';
import MagazineProductTile from './MagazineProductTile';
import PageNavigation from './PageNavigation';
import { useProduct } from '../../contexts/ProductContext';

const MagazineLayout = ({ products, searchTerm, fullscreen = false }) => {
  const { isOpen: isProductOpen } = useProduct();
  const [currentPage, setCurrentPage] = useState(1);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const productsPerPage = 9; // Products per spread - 3x3 grid

  // Define tile layout patterns for 3x3 grid using useMemo
  // All products display vertically
  const layoutPatterns = useMemo(() => [
    {
      // Pattern 1: Large product (2x3), 2 small below, 3 small on right
      name: 'large-with-small',
      tiles: [
        { id: 'A', size: 'large-vertical', gridArea: '1 / 1 / 4 / 3' }, // A spans rows 1-3, cols 1-2
        { id: 'B', size: 'small', gridArea: '1 / 3 / 2 / 4' }, // B
        { id: 'C', size: 'small', gridArea: '2 / 3 / 3 / 4' }, // C
        { id: 'D', size: 'small', gridArea: '3 / 3 / 4 / 4' }, // D
        null, null, null, null, null // Placeholders for 9 products array
      ]
    },
    {
      // Pattern 2: 9 equal small tiles
      name: 'uniform-grid',
      tiles: [
        { id: 'A', size: 'small', gridArea: '1 / 1 / 2 / 2' },
        { id: 'B', size: 'small', gridArea: '1 / 2 / 2 / 3' },
        { id: 'C', size: 'small', gridArea: '1 / 3 / 2 / 4' },
        { id: 'D', size: 'small', gridArea: '2 / 1 / 3 / 2' },
        { id: 'E', size: 'small', gridArea: '2 / 2 / 3 / 3' },
        { id: 'F', size: 'small', gridArea: '2 / 3 / 3 / 4' },
        { id: 'G', size: 'small', gridArea: '3 / 1 / 4 / 2' },
        { id: 'H', size: 'small', gridArea: '3 / 2 / 4 / 3' },
        { id: 'I', size: 'small', gridArea: '3 / 3 / 4 / 4' }
      ]
    },
    {
      // Pattern 3: Center large (2x2) with small around
      name: 'center-focus',
      tiles: [
        { id: 'A', size: 'small', gridArea: '1 / 1 / 2 / 2' },
        { id: 'B', size: 'large-square', gridArea: '1 / 2 / 3 / 4' }, // B spans 2x2
        { id: 'C', size: 'small', gridArea: '2 / 1 / 3 / 2' },
        { id: 'E', size: 'small', gridArea: '3 / 1 / 4 / 2' },
        { id: 'F', size: 'small', gridArea: '3 / 2 / 4 / 3' },
        { id: 'G', size: 'small', gridArea: '3 / 3 / 4 / 4' },
        null, null, null // Placeholders
      ]
    },
    {
      // Pattern 4: Large bottom-left (2x2) with small around
      name: 'bottom-left-focus',
      tiles: [
        { id: 'A', size: 'small', gridArea: '1 / 1 / 2 / 2' },
        { id: 'B', size: 'small', gridArea: '1 / 2 / 2 / 3' },
        { id: 'C', size: 'small', gridArea: '1 / 3 / 2 / 4' },
        { id: 'D', size: 'large-square', gridArea: '2 / 1 / 4 / 3' }, // D spans 2x2
        { id: 'E', size: 'small', gridArea: '2 / 3 / 3 / 4' },
        { id: 'F', size: 'small', gridArea: '3 / 3 / 4 / 4' },
        null, null, null // Placeholders
      ]
    },
    {
      // Pattern 5: Large bottom-right (2x2) with small around
      name: 'bottom-right-focus',
      tiles: [
        { id: 'A', size: 'small', gridArea: '1 / 1 / 2 / 2' },
        { id: 'B', size: 'small', gridArea: '1 / 2 / 2 / 3' },
        { id: 'C', size: 'small', gridArea: '1 / 3 / 2 / 4' },
        { id: 'D', size: 'small', gridArea: '2 / 1 / 3 / 2' },
        { id: 'E', size: 'large-square', gridArea: '2 / 2 / 4 / 4' }, // E spans 2x2
        { id: 'F', size: 'small', gridArea: '3 / 1 / 4 / 2' },
        null, null, null // Placeholders
      ]
    }
  ], []);

  // Group products by category
  const groupedProducts = useMemo(() => {
    const groups = {};
    products.forEach(product => {
      const category = product.category || 'Outros';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(product);
    });
    return groups;
  }, [products]);

  // Create pages with mixed layouts
  const pages = useMemo(() => {
    const allPages = [];
    
    // Sort categories alphabetically
    const sortedCategories = Object.keys(groupedProducts).sort((a, b) => 
      a.localeCompare(b, 'pt', { sensitivity: 'base' })
    );
    
    sortedCategories.forEach(category => {
      const categoryProducts = groupedProducts[category];
      
      // Add category header page with first 3 products from the sequence
      allPages.push({
        type: 'category-header',
        category,
        featuredProducts: categoryProducts.slice(0, 3), // Show first 3 products in sequence
        productCount: categoryProducts.length
      });

      // Add product pages for this category
      let pageIndex = 0;
      for (let i = 0; i < categoryProducts.length; i += productsPerPage) {
        const pageProducts = categoryProducts.slice(i, i + productsPerPage);
        
        // Select layout pattern based on page index (cycles through patterns)
        const layoutPattern = layoutPatterns[pageIndex % layoutPatterns.length];
        
        allPages.push({
          type: 'products',
          layoutPattern,
          products: pageProducts,
          category
        });
        
        pageIndex++;
      }
    });

    return allPages;
  }, [groupedProducts, layoutPatterns]);

  const totalPages = pages.length;
  const currentSpread = pages[currentPage - 1];

  // Create category index for quick navigation
  const categoryIndex = useMemo(() => {
    const index = {};
    pages.forEach((page, idx) => {
      if (page.type === 'category-header') {
        index[page.category] = idx + 1; // Page numbers start at 1
      }
    });
    return index;
  }, [pages]);

  // Get current category
  const currentCategory = useMemo(() => {
    if (!currentSpread) return '';
    return currentSpread.category || '';
  }, [currentSpread]);

  // Navigation handlers
  const handleNextPage = useCallback(() => {
    setCurrentPage(prev => prev < totalPages ? prev + 1 : prev);
  }, [totalPages]);

  const handlePrevPage = useCallback(() => {
    setCurrentPage(prev => prev > 1 ? prev - 1 : prev);
  }, []);

  // Touch handlers for swipe navigation
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      handleNextPage();
    }
    if (isRightSwipe) {
      handlePrevPage();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!fullscreen) return;
    
    const handleKeyDown = (e) => {
      // Don't handle arrow keys if product sidebar is open
      if (isProductOpen) return;
      
      if (e.key === 'ArrowLeft') {
        handlePrevPage();
      }
      if (e.key === 'ArrowRight') {
        handleNextPage();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, fullscreen, totalPages, isProductOpen, handleNextPage, handlePrevPage]);

  // Jump to category
  const handleCategoryJump = (category) => {
    const pageNumber = categoryIndex[category];
    if (pageNumber) {
      setCurrentPage(pageNumber);
    }
  };

  if (!currentSpread) {
    return null;
  }

  const renderCategoryHeader = (page) => (
    <div className="magazine-spread category-header">
      <div className="spread-content">
        <div className="magazine-tile-grid-3x3">
          {/* Category info spanning left 2x3 area */}
          <div 
            className="flex flex-col justify-center items-center p-12 bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-100"
            style={{ gridArea: '1 / 1 / 4 / 3' }} // Spans rows 1-3, columns 1-2
          >
            <h2 className="text-6xl font-light text-bmb-text mb-4">{page.category}</h2>
            <p className="text-xl text-gray-600 mb-8">{page.productCount} produtos</p>
            <div className="w-32 h-0.5 bg-bmb-text"></div>
          </div>
          
          {/* Featured products in right column */}
          {page.featuredProducts.map((product, index) => (
            <div 
              key={product.id} 
              className="overflow-hidden"
              style={{ gridArea: `${index + 1} / 3 / ${index + 2} / 4` }} // Each product takes one cell in column 3
            >
              <MagazineProductTile 
                product={product} 
                size="small"
                searchTerm={searchTerm}
              />
            </div>
          ))}
        </div>
      </div>
      {/* Page numbers */}
      <div className="page-numbers">
        <span className="left-page-number">{currentPage * 2 - 1}</span>
        <span className="right-page-number">{currentPage * 2}</span>
      </div>
    </div>
  );

  const renderProductGrid = (page) => {
    const { layoutPattern, products: pageProducts } = page;
    
    // Filter out null tiles and map products to available tiles
    const activeTiles = layoutPattern.tiles.filter(tile => tile !== null);
    
    return (
      <div className="magazine-spread products-spread">
        <div className="spread-content">
          <div className="magazine-tile-grid-3x3">
            {activeTiles.map((tile, index) => {
              const product = pageProducts[index];
              if (!product) return null;
              
              return (
                <div 
                  key={`${product.id}-${index}`} 
                  className={`tile-wrapper tile-${tile.size}`}
                  style={{ gridArea: tile.gridArea }}
                >
                  <MagazineProductTile
                    product={product}
                    size={tile.size}
                    searchTerm={searchTerm}
                  />
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Page numbers */}
        <div className="page-numbers">
          <span className="left-page-number">{currentPage * 2 - 1}</span>
          <span className="right-page-number">{currentPage * 2}</span>
        </div>
      </div>
    );
  };

  if (fullscreen) {
    return (
      <div 
        className="magazine-container-fullscreen"
        onWheel={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="magazine-spread-fullscreen magazine-spread-transition">
          {currentSpread.type === 'category-header' 
            ? renderCategoryHeader(currentSpread)
            : renderProductGrid(currentSpread)
          }
        </div>
        
        <PageNavigation
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          floating={true}
          categories={categoryIndex}
          currentCategory={currentCategory}
          onCategoryJump={handleCategoryJump}
        />
      </div>
    );
  }

  return (
    <div className="magazine-catalog">
      <div className="magazine-container">
        {currentSpread.type === 'category-header' 
          ? renderCategoryHeader(currentSpread)
          : renderProductGrid(currentSpread)
        }
      </div>
      
      <PageNavigation
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default MagazineLayout;